import axios from 'axios';
import config from '../config/api';

const API_BASE_URL = config.endpoints.tallas;

// Función para formatear fecha
const formatDate = (dateString) => {
  if (!dateString) return 'No disponible';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Fecha inválida';
    
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    return 'Error en fecha';
  }
};

// Mapea objeto del backend (camelCase) al formato esperado por el frontend (UPPER_SNAKE)
const mapFromApi = (item) => {
  const fechaRegistro = item.fechaRegistro || item.FECHA_REGISTRO;
  
  return {
    TALLA_ID: item.tallaId || item.TALLA_ID,
    TALLA_CM: item.tallaCm ?? item.TALLA_CM,
    SOCIA_ID: item.sociaId || item.SOCIA_ID,
    FECHA_REGISTRO: fechaRegistro,
    FECHA_REGISTRO_FORMATTED: formatDate(fechaRegistro),
  };
};

const tallaService = {
  // Obtener todas las tallas con información de socias
  async getAllTallas() {
    try {
      // Obtener tallas y socias en paralelo
      const [tallasResponse, sociosResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/tallas`),
        this.getSociosDisponibles()
      ]);

      const tallas = Array.isArray(tallasResponse.data) ? tallasResponse.data.map(mapFromApi) : [];
      const socios = sociosResponse.success ? sociosResponse.data : [];

      // Crear un mapa de socios por ID para búsqueda rápida
      const sociosMap = {};
      socios.forEach(socio => {
        sociosMap[socio.SOCIA_ID] = socio.SOCIA_NOMBRE;
      });

      // Enriquecer tallas con nombres de socias
      const tallasEnriquecidas = tallas.map(talla => ({
        ...talla,
        SOCIA_NOMBRE: sociosMap[talla.SOCIA_ID] || `SOCIA-${talla.SOCIA_ID}`
      }));

      return { success: true, data: tallasEnriquecidas, message: 'Tallas obtenidas exitosamente' };
    } catch (error) {
      console.error('Error getAllTallas:', error?.response?.data || error.message);
      return { success: false, data: [], message: error?.response?.data?.error || 'Error al obtener las tallas' };
    }
  },

  // Obtener tallas por socia (filtrando en cliente para evitar cambios de backend)
  async getTallasBySocia(sociaId) {
    const resp = await this.getAllTallas();
    if (!resp.success) return resp;
    try {
      const idStr = String(sociaId ?? '').trim();
      const idDigits = idStr.replace(/\D+/g, '');
      const data = resp.data.filter((t) => {
        const src = String(t.SOCIA_ID ?? '').trim();
        if (!src) return false;
        if (src === idStr) return true;
        const srcDigits = src.replace(/\D+/g, '');
        if (srcDigits && srcDigits === idDigits) return true;
        const nSrc = Number(srcDigits);
        const nId = Number(idDigits);
        if (!Number.isNaN(nSrc) && !Number.isNaN(nId) && nSrc === nId) return true;
        return false;
      });
      return { success: true, data, message: 'Tallas de la socia obtenidas exitosamente' };
    } catch (e) {
      console.debug('Filtro robusto de tallas falló:', e?.message);
      return { success: true, data: [], message: 'Tallas de la socia obtenidas exitosamente' };
    }
  },

  // Obtener una talla por ID
  async getTallaById(tallaId) {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/tallas/${tallaId}`);
      return { success: true, data: mapFromApi(data), message: 'Talla obtenida exitosamente' };
    } catch (error) {
      console.error('Error getTallaById:', error?.response?.data || error.message);
      return { success: false, data: null, message: error?.response?.data?.error || 'Error al obtener la talla' };
    }
  },

  // Crear nueva talla
  async createTalla(tallaData) {
    try {
      const payload = {
        sociaId: tallaData.SOCIA_ID,
        tallaCm: typeof tallaData.TALLA_CM === 'string' ? parseFloat(tallaData.TALLA_CM) : tallaData.TALLA_CM,
      };
      const { data } = await axios.post(`${API_BASE_URL}/tallas/registrar`, payload);
      // El backend devuelve el objeto creado en camelCase
      return { success: true, data: mapFromApi(data), message: 'Talla registrada exitosamente' };
    } catch (error) {
      console.error('Error createTalla:', error?.response?.data || error.message);
      return { success: false, data: null, message: error?.response?.data?.error || 'Error al registrar la talla' };
    }
  },

  // Actualizar talla
  async updateTalla(tallaId, tallaData) {
    try {
      const payload = {
        tallaCm: typeof tallaData.TALLA_CM === 'string' ? parseFloat(tallaData.TALLA_CM) : tallaData.TALLA_CM,
      };
      const { data } = await axios.put(`${API_BASE_URL}/tallas/${tallaId}`, payload);
      return { success: true, data: mapFromApi(data), message: 'Talla actualizada exitosamente' };
    } catch (error) {
      console.error('Error updateTalla:', error?.response?.data || error.message);
      return { success: false, data: null, message: error?.response?.data?.error || 'Error al actualizar la talla' };
    }
  },

  // Eliminar talla
  async deleteTalla(tallaId) {
    try {
      await axios.delete(`${API_BASE_URL}/tallas/${tallaId}`);
      return { success: true, message: 'Talla eliminada exitosamente' };
    } catch (error) {
      console.error('Error deleteTalla:', error?.response?.data || error.message);
      return { success: false, message: error?.response?.data?.error || 'Error al eliminar la talla' };
    }
  },

  // Obtener estadísticas simples
  async getEstadisticas() {
    const resp = await this.getAllTallas();
    if (!resp.success) return { success: false, data: { total: 0, socias: 0, esteAño: 0 }, message: resp.message };
    try {
      const tallas = resp.data;
      const totalTallas = tallas.length;
      const totalSocias = [...new Set(tallas.map((t) => t.SOCIA_ID))].length;
      const thisYear = new Date().getFullYear();
      const tallasEsteAño = tallas.filter((t) => new Date(t.FECHA_REGISTRO).getFullYear() === thisYear).length;
      return { success: true, data: { total: totalTallas, socias: totalSocias, esteAño: tallasEsteAño }, message: 'Estadísticas obtenidas exitosamente' };
    } catch (error) {
      return { success: false, data: { total: 0, socias: 0, esteAño: 0 }, message: 'Error al calcular estadísticas' };
    }
  },

  // Obtener socios disponibles para registrar tallas
  async getSociosDisponibles() {
    try {
      // Configurar URLs posibles
      const urls = [
        'http://localhost:3000/api/socias',
        `${config.endpoints.socias}/api/socias`,
        `${config.API_BASE_URL}/socias`,
        `${config.API_URL}/api/socias`
      ];
      
      let response = null;
      
      // Intentar diferentes endpoints
      for (const url of urls) {
        try {
          response = await axios.get(url);
          break;
        } catch (err) {
          continue;
        }
      }
      
      if (!response) {
        throw new Error('No se pudo conectar a ningún endpoint de socias');
      }
      
      // La API devuelve directamente un array de socias
      const socias = Array.isArray(response.data) ? response.data : [];
      
      // Función para convertir array MySQL a objeto basada en posiciones fijas
      const convertMysqlArrayToObject = (item) => {
        if (!Array.isArray(item)) return item; // Ya es un objeto
        
        // Mapeo basado en la estructura real de la tabla SOCIA
        // Posiciones extraídas de los logs del componente TestSocias
        const objeto = {
          SOCIA_ID: item[0],           // ID numérico
          TS: item[1],                 // Timestamp
          MENTOR_ID: item[2],          // ID del mentor
          SOLICITUDINGRESO_ID: item[3], // ID solicitud
          CARNET_ID: item[4],          // ID carnet
          USUARIO_ID: item[5],         // ID usuario
          PROFESION_ID: item[6],       // ID profesión
          SOCIA_FOTO_URL: item[7],     // URL foto
          SOCIA_APELLIDO: item[8],     // Apellido
          SOCIA_NOMBRE: item[9],       // Nombre
          SOCIA_FECHANACIMIENTO: item[10], // Fecha nacimiento
          SOCIA_NACIONALIDAD: item[11], // Nacionalidad
          SOCIA_CEDULA: item[12],      // Cédula
          SOCIA_ESTADOCIVIL: item[13], // Estado civil
          SOCIA_FECHAINSCRIPCION: item[14], // Fecha inscripción
          SOCIA_TIPO_SINDROME: item[15], // Tipo síndrome
          SOCIA_CARIOTIPO: item[16],   // Cariotipo
          SOCIA_CARNETDISCAPACIDAD: item[17], // Carnet discapacidad
          SOCIA_TIPODISCAPACIDAD: item[18], // Tipo discapacidad
          SOCIA_PORCENTAJEDISCAPACIDAD: item[19], // % discapacidad
          SOCIA_GRADO: item[20],       // Grado
          SOCIA_IEES: item[21],        // IEES
          SOCIA_EDADDETECCION: item[22], // Edad detección
          SOCIA_HORMONACRECIMIENTO: item[23], // Hormona crecimiento
          SOCIA_MSP: item[24],         // MSP
          SOCIA_ESTADO: item[25],      // Estado (¡IMPORTANTE!)
          SOCIA_DIRECCION: item[26],   // Dirección
          SOCIA_PAIS: item[27],        // País
          SOCIA_PROVINCIA: item[28],   // Provincia
          SOCIA_CIUDAD: item[29],      // Ciudad
          SOCIA_SECTOR: item[30],      // Sector
          SOCIA_TELEFONO1: item[31],   // Teléfono 1
          SOCIA_TELEFONO2: item[32],   // Teléfono 2
          SOCIA_EMAIL: item[33],       // Email
          SOCIA_RFacebook: item[34],   // Facebook
          SOCIA_RInstagram: item[35],  // Instagram
          SOCIA_RX: item[36],          // RX
          SOCIA_EDAD: item[37],        // Edad
          SOCIA_SEXO: item[38],        // Sexo
          SOCIA_REPRESENTANTE: item[39], // Representante
          SOCIA_REPRESENTANTECEDULA: item[40], // Cédula representante
          SOCIA_REPRESENTANTENOMBRE: item[41], // Nombre representante
          SOCIA_MENTORESTADO: item[42], // Estado mentor
          SOCIA_FOTO_MIME: item[43],   // MIME foto
          SOCIA_TALLA: item[44],       // Talla
          NOMBRE_MENTOR_ASIGNADO: item[45], // Nombre mentor asignado
          ES_MENTOR: item[46],         // Es mentor
          MENTOR_DISPLAY: item[47]     // Display mentor
        };
        
        return objeto;
      };
      


      // Procesar socias: convertir arrays SI es necesario, luego filtrar
      const sociasProcesadas = socias.map(socia => {
        // Solo convertir si es array, sino usar el objeto tal como viene
        return Array.isArray(socia) ? convertMysqlArrayToObject(socia) : socia;
      });
      
      // Filtrar solo socias activas
      const sociasMapeadas = sociasProcesadas
        .filter(socia => {
          // Buscar el campo de estado con diferentes nombres posibles
          const estado = socia.SOCIA_ESTADO || socia.estado || socia.ESTADO || socia.sociaEstado;
          const isActivo = estado === 'Activo';
          return isActivo;
        })
        .map(socia => {
          // Obtener valores con nombres de propiedades flexibles
          const id = socia.SOCIA_ID || socia.id || socia.ID || socia.sociaId;
          const nombre = socia.SOCIA_NOMBRE || socia.nombre || socia.NOMBRE || socia.sociaNombre;
          const apellido = socia.SOCIA_APELLIDO || socia.apellido || socia.APELLIDO || socia.sociaApellido;
          const cedula = socia.SOCIA_CEDULA || socia.cedula || socia.CEDULA || socia.sociaCedula;
          
          // Construir nombre completo
          const nombreCompleto = `${nombre || ''} ${apellido || ''}`.trim();
          
          const mapped = {
            SOCIA_ID: id,
            SOCIA_NOMBRE: nombreCompleto,
            CEDULA: cedula
          };
          return mapped;
        })
        .filter(socia => socia.SOCIA_NOMBRE && socia.SOCIA_NOMBRE.trim() !== '');

      return { 
        success: true, 
        data: sociasMapeadas, 
        message: `${sociasMapeadas.length} socias disponibles cargadas exitosamente` 
      };
    } catch (error) {
      console.error('=== ERROR EN getSociosDisponibles ===');
      console.error('Mensaje:', error.message);
      console.error('Stack:', error.stack);
      
      // Como último recurso, devolver datos de prueba
      const sociasPrueba = [
        { SOCIA_ID: 'TEST-001', SOCIA_NOMBRE: 'María García Ejemplo', CEDULA: '1234567890' },
        { SOCIA_ID: 'TEST-002', SOCIA_NOMBRE: 'Ana López Ejemplo', CEDULA: '0987654321' },
        { SOCIA_ID: 'TEST-003', SOCIA_NOMBRE: 'Carmen Rodríguez Ejemplo', CEDULA: '1122334455' }
      ];
      
      console.log('=== USANDO DATOS DE PRUEBA ===');
      sociasPrueba.forEach((socia, index) => {
        console.log(`${index + 1}. ${socia.SOCIA_NOMBRE} (${socia.SOCIA_ID}) - ${socia.CEDULA}`);
      });
      
      return { 
        success: true, 
        data: sociasPrueba, 
        message: 'Usando datos de prueba - verificar conexión con API de socias' 
      };
    }
  },
};

export default tallaService;